import type { Account, Holding } from '@/api/generated/model';
import type { AssetClass } from '@/api/generated/model/assetClass';

export interface PositionRow {
  holding: Holding;
  account: Account;
}

export function flattenPositions(
  accounts: Account[],
  holdingsByAccount: Record<string, Holding[]>
): PositionRow[] {
  const tradingAccounts = accounts.filter((a) => a.capabilities?.includes('trading'));
  const rows: PositionRow[] = [];
  for (const account of tradingAccounts) {
    for (const holding of holdingsByAccount[account.id] ?? []) {
      rows.push({ holding, account });
    }
  }
  return rows.sort((a, b) => (b.holding.marketValue ?? 0) - (a.holding.marketValue ?? 0));
}

export interface AssetClassTotal {
  assetClass: AssetClass;
  marketValue: number;
  unrealizedPnL: number;
  count: number;
}

export function aggregateHoldingsByAssetClass(holdings: Holding[]): AssetClassTotal[] {
  const map = new Map<AssetClass, AssetClassTotal>();
  for (const holding of holdings) {
    const assetClass = holding.assetClass ?? 'other';
    const existing = map.get(assetClass) ?? {
      assetClass,
      marketValue: 0,
      unrealizedPnL: 0,
      count: 0,
    };
    existing.marketValue += holding.marketValue ?? holding.costBasis;
    existing.unrealizedPnL += holding.unrealizedPnL ?? 0;
    existing.count += 1;
    map.set(assetClass, existing);
  }
  return [...map.values()].sort((a, b) => b.marketValue - a.marketValue);
}

export function portfolioTotals(positions: PositionRow[]): {
  marketValue: number;
  unrealizedPnL: number;
} {
  return positions.reduce(
    (acc, { holding }) => ({
      marketValue: acc.marketValue + (holding.marketValue ?? holding.costBasis),
      unrealizedPnL: acc.unrealizedPnL + (holding.unrealizedPnL ?? 0),
    }),
    { marketValue: 0, unrealizedPnL: 0 }
  );
}
